import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { signalRService, ConnectionState } from '../services/signalr/SignalRService';
import { HubEvents, HubMethods, UserPresence, SignalRRoom, SignalRPlayer, ChatMessage } from '../services/signalr/types';

interface SignalRContextValue {
  // Connection state
  connectionState: ConnectionState;
  isConnected: boolean;
  connectionId: string | null;

  // Connection management
  connect: (userId?: string, accessToken?: string) => Promise<void>;
  disconnect: () => Promise<void>;

  // User & Presence
  currentUserId: string | null;
  onlineUsers: UserPresence[];
  updatePresence: (status: 'online' | 'away' | 'busy' | 'offline') => Promise<void>;

  // Rooms
  rooms: SignalRRoom[];
  currentRoom: SignalRRoom | null;
  createRoom: (room: Partial<SignalRRoom>) => Promise<SignalRRoom>;
  joinRoom: (roomId: string) => Promise<SignalRRoom>;
  leaveRoom: () => Promise<void>;
  refreshRooms: () => Promise<void>;

  // Chat
  sendChatMessage: (content: string, channelId?: string) => Promise<void>;
  sendPrivateMessage: (toUserId: string, content: string) => Promise<void>;

  // Event subscriptions
  subscribe: <T extends unknown[]>(event: string, handler: (...args: T) => void) => () => void;

  // Direct invoke for custom methods
  invoke: <T = void>(methodName: string, ...args: unknown[]) => Promise<T>;
}

const SignalRContext = createContext<SignalRContextValue | null>(null);

export function SignalRProvider({ children }: { children: React.ReactNode }) {
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [onlineUsers, setOnlineUsers] = useState<UserPresence[]>([]);
  const [rooms, setRooms] = useState<SignalRRoom[]>([]);
  const [currentRoom, setCurrentRoom] = useState<SignalRRoom | null>(null);

  const cleanupFunctions = useRef<(() => void)[]>([]);

  // Set up SignalR event handlers
  useEffect(() => {
    signalRService.setEventHandlers({
      onConnectionStateChange: setConnectionState,
      onError: (error) => console.error('[SignalR] Error:', error),
      onReconnecting: () => console.log('[SignalR] Reconnecting...'),
      onReconnected: (id) => console.log('[SignalR] Reconnected:', id),
    });

    return () => {
      // Clean up all subscriptions
      cleanupFunctions.current.forEach((cleanup) => cleanup());
      cleanupFunctions.current = [];
    };
  }, []);

  // Set up hub event listeners when connected
  useEffect(() => {
    if (connectionState !== 'connected') return;

    const unsubscribers: (() => void)[] = [];

    // Room events
    unsubscribers.push(
      signalRService.on<[SignalRRoom]>(HubEvents.RoomCreated, (room) => {
        setRooms((prev) => [...prev, room]);
      })
    );

    unsubscribers.push(
      signalRService.on<[SignalRRoom]>(HubEvents.RoomUpdated, (room) => {
        setRooms((prev) => prev.map((r) => (r.id === room.id ? room : r)));
        if (currentRoom?.id === room.id) {
          setCurrentRoom(room);
        }
      })
    );

    unsubscribers.push(
      signalRService.on<[string]>(HubEvents.RoomDeleted, (roomId) => {
        setRooms((prev) => prev.filter((r) => r.id !== roomId));
        if (currentRoom?.id === roomId) {
          setCurrentRoom(null);
        }
      })
    );

    unsubscribers.push(
      signalRService.on<[SignalRRoom[]]>(HubEvents.RoomListUpdated, (roomList) => {
        setRooms(roomList);
      })
    );

    // Player joined room - server sends (roomId: string, playerJson: string)
    unsubscribers.push(
      signalRService.on<[string, string | SignalRPlayer]>(
        HubEvents.PlayerJoinedRoom,
        (roomId, playerData) => {
          // Parse JSON string if needed (server sends string for Unity compatibility)
          let player: SignalRPlayer;
          if (typeof playerData === 'string') {
            try {
              player = JSON.parse(playerData) as SignalRPlayer;
            } catch (e) {
              console.error('[SignalRContext] Failed to parse PlayerJoinedRoom data:', e);
              return;
            }
          } else {
            player = playerData as SignalRPlayer;
          }
          
          console.log('[SignalRContext] PlayerJoinedRoom:', roomId, player);
          setRooms((prev) =>
            prev.map((r) => {
              if (r.id === roomId) {
                return {
                  ...r,
                  players: [...r.players.filter((p) => p.id !== player.id), player],
                  currentPlayers: r.players.filter((p) => p.id !== player.id).length + 1,
                };
              }
              return r;
            })
          );
          if (currentRoom?.id === roomId) {
            setCurrentRoom((prev) =>
              prev
                ? {
                    ...prev,
                    players: [...prev.players.filter((p) => p.id !== player.id), player],
                    currentPlayers: prev.players.filter((p) => p.id !== player.id).length + 1,
                  }
                : null
            );
          }
        }
      )
    );

    // Player left room - server sends (roomId: string, playerId: string)
    unsubscribers.push(
      signalRService.on<[string, string]>(
        HubEvents.PlayerLeftRoom,
        (roomId, playerId) => {
          console.log('[SignalRContext] PlayerLeftRoom:', roomId, playerId);
          setRooms((prev) =>
            prev.map((r) => {
              if (r.id === roomId) {
                return {
                  ...r,
                  players: r.players.filter((p) => p.id !== playerId),
                  currentPlayers: r.players.filter((p) => p.id !== playerId).length,
                };
              }
              return r;
            })
          );
          if (currentRoom?.id === roomId) {
            setCurrentRoom((prev) =>
              prev
                ? {
                    ...prev,
                    players: prev.players.filter((p) => p.id !== playerId),
                    currentPlayers: prev.players.filter((p) => p.id !== playerId).length,
                  }
                : null
            );
          }
        }
      )
    );

    // Player spawned in game - server sends (roomId: string, playerDataJson: string)
    // Unity broadcasts this when a player's character is spawned in the game world
    unsubscribers.push(
      signalRService.on<[string, string]>(
        HubEvents.PlayerSpawned,
        (roomId, playerDataJson) => {
          console.log('[SignalRContext] PlayerSpawned:', roomId, playerDataJson);
          // This event is primarily for Unity clients to sync character spawns
          // React app can use this to update UI if needed (e.g., show player ready state)
        }
      )
    );

    // No-op handlers for Unity-to-Unity sync messages
    // These are broadcast by the server but only meaningful to Unity clients
    // Registering them suppresses "No client method found" warnings
    unsubscribers.push(
      signalRService.on<[Uint8Array]>('ReceiveNetworkPayload', () => {
        // Unity network sync payload - not needed by React
      })
    );
    unsubscribers.push(
      signalRService.on<[string]>('PlayerLeftGame', () => {
        // Unity game state event - handled by Unity
      })
    );

    // Presence events
    unsubscribers.push(
      signalRService.on<[UserPresence]>(HubEvents.UserOnline, (presence) => {
        setOnlineUsers((prev) => [...prev.filter((u) => u.userId !== presence.userId), presence]);
      })
    );

    unsubscribers.push(
      signalRService.on<[string]>(HubEvents.UserOffline, (userId) => {
        setOnlineUsers((prev) => prev.filter((u) => u.userId !== userId));
      })
    );

    unsubscribers.push(
      signalRService.on<[UserPresence]>(HubEvents.PresenceUpdated, (presence) => {
        setOnlineUsers((prev) =>
          prev.map((u) => (u.userId === presence.userId ? presence : u))
        );
      })
    );

    cleanupFunctions.current = unsubscribers;

    // Fetch initial room list
    refreshRooms();

    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [connectionState, currentRoom?.id]);

  const connect = useCallback(async (userId?: string, accessToken?: string) => {
    await signalRService.connect(userId, accessToken);
    if (userId) {
      setCurrentUserId(userId);
    }
  }, []);

  const disconnect = useCallback(async () => {
    await signalRService.disconnect();
    setCurrentUserId(null);
    setCurrentRoom(null);
  }, []);

  const updatePresence = useCallback(async (status: 'online' | 'away' | 'busy' | 'offline') => {
    // Map string status to enum value (backend expects: 0=Online, 1=Away, 2=Busy, 3=Offline)
    const statusMap: Record<string, number> = { online: 0, away: 1, busy: 2, offline: 3 };
    await signalRService.invoke(HubMethods.UpdatePresence, {
      userId: currentUserId,
      status: statusMap[status] ?? 0,
      lastSeen: new Date().toISOString(),
    });
  }, [currentUserId]);

  const createRoom = useCallback(async (room: Partial<SignalRRoom>): Promise<SignalRRoom> => {
    const createdRoom = await signalRService.invoke<SignalRRoom>(HubMethods.CreateRoom, room);
    setRooms((prev) => [...prev, createdRoom]);
    return createdRoom;
  }, []);

  const joinRoom = useCallback(async (roomId: string): Promise<SignalRRoom> => {
    const room = await signalRService.invoke<SignalRRoom>(HubMethods.JoinRoom, roomId);
    setCurrentRoom(room);
    return room;
  }, []);

  const leaveRoom = useCallback(async () => {
    if (currentRoom) {
      await signalRService.invoke(HubMethods.LeaveRoom, currentRoom.id);
      setCurrentRoom(null);
    }
  }, [currentRoom]);

  const refreshRooms = useCallback(async () => {
    try {
      const roomList = await signalRService.invoke<SignalRRoom[]>(HubMethods.GetRooms);
      setRooms(roomList);
    } catch (error) {
      console.error('[SignalR] Failed to fetch rooms:', error);
    }
  }, []);

  const sendChatMessage = useCallback(async (content: string, channelId?: string) => {
    await signalRService.invoke(HubMethods.SendChatMessage, content, channelId);
  }, []);

  const sendPrivateMessage = useCallback(async (toUserId: string, content: string) => {
    await signalRService.invoke(HubMethods.SendPrivateMessage, toUserId, content);
  }, []);

  const subscribe = useCallback(<T extends unknown[]>(event: string, handler: (...args: T) => void) => {
    return signalRService.on(event, handler);
  }, []);

  const invoke = useCallback(async <T = void>(methodName: string, ...args: unknown[]): Promise<T> => {
    return signalRService.invoke<T>(methodName, ...args);
  }, []);

  const value: SignalRContextValue = {
    connectionState,
    isConnected: connectionState === 'connected',
    connectionId: signalRService.connectionId,
    connect,
    disconnect,
    currentUserId,
    onlineUsers,
    updatePresence,
    rooms,
    currentRoom,
    createRoom,
    joinRoom,
    leaveRoom,
    refreshRooms,
    sendChatMessage,
    sendPrivateMessage,
    subscribe,
    invoke,
  };

  return <SignalRContext.Provider value={value}>{children}</SignalRContext.Provider>;
}

export function useSignalR(): SignalRContextValue {
  const context = useContext(SignalRContext);
  if (!context) {
    throw new Error('useSignalR must be used within a SignalRProvider');
  }
  return context;
}

// Specialized hooks for specific features
export function useSignalRRooms() {
  const { rooms, currentRoom, createRoom, joinRoom, leaveRoom, refreshRooms, isConnected } =
    useSignalR();
  return { rooms, currentRoom, createRoom, joinRoom, leaveRoom, refreshRooms, isConnected };
}

export function useSignalRPresence() {
  const { onlineUsers, updatePresence, isConnected } = useSignalR();
  return { onlineUsers, updatePresence, isConnected };
}

export function useSignalRChat() {
  const { sendChatMessage, sendPrivateMessage, subscribe, isConnected } = useSignalR();
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  useEffect(() => {
    if (!isConnected) return;

    const unsubscribe = subscribe<[ChatMessage]>(HubEvents.ReceiveChatMessage, (message) => {
      setMessages((prev) => [...prev, message]);
    });

    return unsubscribe;
  }, [isConnected, subscribe]);

  return { messages, sendChatMessage, sendPrivateMessage, isConnected };
}
